import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./NavigationLayout.css";
import DigicommMegaMenu from "./DigicommMegaMenu";
import MobileMenu from "../MobileMenu/MobileMenu";

const logo = `${process.env.PUBLIC_URL}/images/logo/Group%201000007815.png`;

const Header: React.FC = () => {
  const [isSticky, setIsSticky] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [mobileActive, setMobileActive] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);

  const lastScrollY = useRef<number>(0);

  useEffect(() => {
    let stickyTimer: ReturnType<typeof setTimeout> | undefined;

    const handleScroll = () => {
      const currentScroll = window.scrollY;

      if (stickyTimer !== undefined) {
        clearTimeout(stickyTimer);
      }

      if (currentScroll <= 100) {
        setIsSticky(false);
        setIsVisible(false);
      } else if (currentScroll < lastScrollY.current) {
        setIsSticky(true);
        stickyTimer = setTimeout(() => setIsVisible(true), 10);
      } else if (currentScroll > lastScrollY.current) {
        setIsVisible(false);
        stickyTimer = setTimeout(() => setIsSticky(false), 100);
      }

      lastScrollY.current = currentScroll;
    };

    lastScrollY.current = window.scrollY;
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);

      if (stickyTimer !== undefined) {
        clearTimeout(stickyTimer);
      }
    };
  }, []);

  const handleClick = () => {
    setMobileActive(false);
    setResourcesOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  return (
    <header
      id="xb-header-area"
      className="header-area header-style--one header-transparent is-sticky"
    >
      <div
        className={`xb-header xb-sticky-stt ${
          isSticky ? "xb-header-area-sticky" : ""
        } ${isVisible ? "xb-header-fixed" : "xb-header-hidden"}`}
      >
        <div className="container mxw-1650">
          <div className="header__wrap ul_li_between">
            {/* Logo */}
            <div className="xb-header-logo">
              <Link to="/" className="logo1" onClick={handleClick}>
                <img src={logo} alt="Digicomm" width="4191" height="918" className="digicomm-brand-logo" />
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className="main-menu__wrap navbar navbar-expand-lg p-0">
              <nav
                className="main-menu collapse navbar-collapse"
                aria-label="Primary navigation"
              >
                <ul className="digicomm-primary-menu">
                  <li className="active">
                    <Link to="/" onClick={handleClick}>
                      <span>Home</span>
                    </Link>
                  </li>

                  <li className="menu-item-has-children">
                    <Link to="/about" onClick={handleClick}>
                      <span>About</span>
                    </Link>

                    <ul className="submenu digicomm-submenu">
                      <li>
                        <Link to="/about" onClick={handleClick}>
                          About Digicomm
                        </Link>
                      </li>
                      <li>
                        <Link to="/team" onClick={handleClick}>
                          Leadership
                        </Link>
                      </li>
                      <li>
                        <Link to="/why-digicomm" onClick={handleClick}>
                          Why Digicomm
                        </Link>
                      </li>
                    </ul>
                  </li>

                  <li className="menu-item-has-children digicomm-services-menu">
                    <Link to="/service" onClick={handleClick}>
                      <span>Services</span>
                    </Link>

                    <DigicommMegaMenu onNavigate={handleClick} />
                  </li>

                  <li className="menu-item-has-children">
                    <Link to="/solutions" onClick={handleClick}>
                      <span>Solutions</span>
                    </Link>

                    <ul className="submenu digicomm-submenu digicomm-submenu--solutions">
                      <li>
                        <Link
                          to="/solutions/engineering-team-extension"
                          onClick={handleClick}
                        >
                          Engineering Team Extension
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/solutions/project-ownership"
                          onClick={handleClick}
                        >
                          Project Ownership &amp; Managed Engineering
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/solutions/end-to-end-silicon-development"
                          onClick={handleClick}
                        >
                          End-to-End Silicon Development
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/solutions/engineering-product-design"
                          onClick={handleClick}
                        >
                          Engineering Productivity &amp; Design Automation
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/solutions/product-realization"
                          onClick={handleClick}
                        >
                          Product Realization &amp; Deployment
                        </Link>
                      </li>
                    </ul>
                  </li>

                  <li>
                    <Link to="/ip-accelerators" onClick={handleClick}>
                      <span>IP &amp; Accelerators</span>
                    </Link>
                  </li>

                  <li>
                    <Link to="/industries" onClick={handleClick}>
                      <span>Industries</span>
                    </Link>
                  </li>

                  <li className={`menu-item-has-children menu-last ${resourcesOpen ? "resources-open" : ""}`}
                    onMouseLeave={() => setResourcesOpen(false)}
                    onBlur={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setResourcesOpen(false);
                    }}
                    onKeyDown={(event) => { if (event.key === "Escape") setResourcesOpen(false); }}>
                    <a href="#resources-dropdown" role="button" aria-expanded={resourcesOpen} aria-controls="resources-dropdown"
                      onClick={(event) => { event.preventDefault(); setResourcesOpen(!resourcesOpen); }}
                      onKeyDown={(event) => {
                        if (event.key === " ") { event.preventDefault(); setResourcesOpen(!resourcesOpen); }
                      }}>
                      <span>Resources</span>
                    </a>

                    <ul id="resources-dropdown" className="submenu digicomm-submenu">
                      <li>
                        <Link to="/blog" onClick={handleClick}>
                          Insights &amp; Articles
                        </Link>
                      </li>
                      <li>
                        <Link to="/career" onClick={handleClick}>
                          Careers
                        </Link>
                      </li>
                    </ul>
                  </li>

                  <li>
                    <Link to="/contact" onClick={handleClick}>
                      <span>Contact</span>
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>

            {/* Contact Button */}
            <div className="header-btn">
              <Link
                to="/contact"
                className="thm-btn"
                onClick={handleClick}
              >
                Let's Talk
              </Link>
            </div>

            {/* Mobile Toggle */}
            <div className="header-bar-mobile side-menu d-lg-none">
              <button
                type="button"
                className="xb-nav-mobile"
                aria-label="Toggle navigation"
                aria-expanded={mobileActive}
                aria-controls="digicomm-mobile-navigation"
                onClick={() => setMobileActive((active) => !active)}
              >
                <i className="far fa-bars" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div className="xb-header-wrap">
            <div
              id="digicomm-mobile-navigation"
              className={`xb-header-menu ${mobileActive ? "active" : ""}`}
            >
              <div className="xb-header-menu-scroll">
                <div
                  className="xb-menu-close xb-hide-xl xb-close"
                  role="button"
                  tabIndex={0}
                  aria-label="Close navigation"
                  onClick={() => setMobileActive(false)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setMobileActive(false);
    setResourcesOpen(false);
                    }
                  }}
                ></div>

                <div className="xb-logo-mobile xb-hide-xl">
                  <Link to="/" rel="home" onClick={handleClick}>
                    <img src={logo} alt="Digicomm" width="4191" height="918" className="digicomm-brand-logo" />
                  </Link>
                </div>

                <div className="xb-header-mobile-search xb-hide-xl">
                  <form role="search" onSubmit={handleSubmit}>
                    <input
                      type="text"
                      placeholder="Search..."
                      name="s"
                      className="search-field"
                      aria-label="Search"
                    />
                    <button
                      className="search-submit"
                      type="submit"
                      aria-label="Submit search"
                    >
                      <i className="far fa-search" aria-hidden="true"></i>
                    </button>
                  </form>
                </div>

                <nav
                  className="xb-header-nav"
                  aria-label="Mobile navigation"
                  onClick={(event) => {
                    if ((event.target as HTMLElement).closest("a")) {
                      setMobileActive(false);
    setResourcesOpen(false);
                    }
                  }}
                >
                  <MobileMenu />
                </nav>
              </div>
            </div>

            <div
              className="xb-header-menu-backdrop"
              onClick={() => setMobileActive(false)}
            ></div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;